import { useCallback, useEffect, useState } from "react";
import { KeyRound, Trash2, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { api, errMsg } from "@/lib/api";
import { fmtDate, generatePassword } from "@/lib/format";
import { InviteOrPassword, inviteToast } from "@/components/InviteOrPassword";
import { InviteBadge } from "@/components/InviteBadge";

const TeamRow = ({ cid, u, isMe, reload }) => {
  const [edit, setEdit] = useState(false);
  const [pw, setPw] = useState("");
  const savePw = async () => {
    try {
      await api.patch(`/customers/${cid}/users/${u.id}`, { password: pw });
      toast.success(`Nytt passord er satt for ${u.email}`);
      setEdit(false);
      setPw("");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const del = async () => {
    if (!window.confirm(`Fjerne brukeren ${u.email}? Personen kan ikke lenger logge inn.`)) return;
    try {
      await api.delete(`/customers/${cid}/users/${u.id}`);
      toast.success("Brukeren er fjernet");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-3 sm:p-4 space-y-3" data-testid={`team-row-${u.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-medium text-slate-900 truncate">{u.name || u.email}{isMe && <span className="ml-2 text-xs text-sky-700 bg-sky-50 px-1.5 py-0.5 rounded">Deg</span>}</div>
          <div className="text-xs text-slate-500 truncate">{u.email} · Sist innlogget {fmtDate(u.last_login)}</div>
        </div>
        {!isMe && (
          <div className="flex shrink-0">
            <Button size="sm" variant="ghost" onClick={() => setEdit(!edit)} title="Sett nytt passord" data-testid={`team-${u.id}-change-password`}><KeyRound className="w-4 h-4" /></Button>
            <Button size="sm" variant="ghost" onClick={del} className="text-slate-500 hover:text-red-600" title="Fjern bruker" data-testid={`team-${u.id}-delete`}><Trash2 className="w-4 h-4" /></Button>
          </div>
        )}
      </div>
      {u.invite_pending && <InviteBadge url={`/customers/${cid}/users/${u.id}/invite`} testId={`team-${u.id}-invite`} />}
      {edit && (
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="flex-1"><PasswordInput value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Nytt passord (min. 8 tegn)" autoComplete="new-password" testId={`team-${u.id}-new-password`} /></div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setPw(generatePassword())}>Generer</Button>
            <Button onClick={savePw} disabled={pw.length < 8} data-testid={`team-${u.id}-save-password`}>Lagre</Button>
          </div>
        </div>
      )}
    </div>
  );
};

export const TeamPanel = ({ cid, meId }) => {
  const [users, setUsers] = useState([]);
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [invite, setInvite] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/customers/${cid}/users`);
      setUsers(data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  }, [cid]);

  useEffect(() => {
    load();
  }, [load]);

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/customers/${cid}/users`, { ...f, password: invite ? null : f.password });
      inviteToast(toast, f.email, data.invite_sent);
      setF({ name: "", email: "", password: "" });
      load();
    } catch (ex) {
      toast.error(errMsg(ex));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="team-panel">
      {users.map((u) => <TeamRow key={u.id} cid={cid} u={u} isMe={u.id === meId} reload={load} />)}
      <form onSubmit={add} className="bg-slate-50 border border-dashed border-slate-300 rounded-lg p-3 sm:p-4 space-y-3" data-testid="team-add-form">
        <div className="text-sm font-medium text-slate-800">Legg til ny bruker</div>
        <div className="grid sm:grid-cols-2 gap-2">
          <Input placeholder="Navn" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="team-add-name" />
          <Input placeholder="E-post" type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="team-add-email" />
        </div>
        <InviteOrPassword invite={invite} setInvite={setInvite} password={f.password} setPassword={(v) => setF({ ...f, password: v })} testPrefix="team-add" />
        <Button type="submit" disabled={busy} data-testid="team-add-submit"><UserPlus className="w-4 h-4 mr-1" />{invite ? "Send invitasjon" : "Legg til"}</Button>
      </form>
    </div>
  );
};
